import React, { useState, useEffect, useRef } from 'react';
import { Redirect } from 'react-router-dom';
import Select from 'react-select';
import 'react-select/dist/react-select.css';
import Swal from 'sweetalert2';
import { FormattedMessage, useIntl } from 'react-intl';
import DropzoneComponent from 'react-dropzone-component/dist/react-dropzone';
import 'react-dropzone-component/styles/filepicker.css';
import 'dropzone/dist/min/dropzone.min.css';
import '../../assets/css/vec-dropzone.css';
import Config from '../../commons/config/Config.js';
import Security from '../../commons/security/Security.js';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';
import * as Utils from '../../commons/utils/Utils';
import Loading from '../ui/Loading.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import LocaleFormat from '../../commons/utils/LocaleFormat.js';
import { formatearFechaHoraTurno } from '../correctivos/fechaHoraTurnoAlta';

const INMOVILIZADO_SI = 1;
const INMOVILIZADO_NO = 2;

const formDataInicial = {
  movil: null,
  inmovilizado: null,
  detalle: '',
  fechaHoraTurno: null,
};

const SolicitudesAbm = () => {
  const intl = useIntl();
  const dropzone = useRef(null);
  const [redirectTo, setRedirectTo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(formDataInicial);
  const [adjuntos, setAdjuntos] = useState([]);
  const inmovilizadoHabilitado = ConfigBusiness.get('tickets.inmovilizado.habilitado') === 'true';
  const fechaTurnoHabilitada = ConfigBusiness.get('mantenimientos.correctivo.fechaTurno.habilitado') === 'true'
    && ConfigBusiness.get('mantenimientos.correctivo.gerenciadores.habilitado') === 'true'
    && ConfigBusiness.get('tickets.motivoAsignacion.habilitado') !== 'true';

  useEffect(() => {
    if (ConfigBusiness.get('mantenimientos.solicitudes.habilitado') !== 'true' || !Security.hasPermission('SOLICITUDES_CREAR')) {
      setRedirectTo('/error');
    }
  }, []);

  const inmovilizadoOptions = [
    { value: INMOVILIZADO_NO, label: intl.formatMessage({ id: 'correctivosAbm.inmovilizadoOptions.no', defaultMessage: 'NO' }) },
    { value: INMOVILIZADO_SI, label: intl.formatMessage({ id: 'correctivosAbm.inmovilizadoOptions.si', defaultMessage: 'SI' }) },
  ];

  const buscarMoviles = (search) =>
    Utils.getData('/moviles/simple-search?search=' + encodeURIComponent(search || ''))
      .then((moviles) => ({ options: moviles, complete: true }))
      .catch(() => ({ options: [], complete: true }));

  const dropzoneConfig = {
    showFiletypeIcon: false,
    postUrl: Config.get('apiUrlBase') + '/adjuntos',
  };

  const dropzoneDjsConfig = {
    addRemoveLinks: false,
    uploadMultiple: false,
    parallelUploads: 1,
    maxFilesize: 20,
    dictDefaultMessage: intl.formatMessage({ id: 'solicitudesAbm.adjuntos.dropzone', defaultMessage: 'Haga click aquí o arrastre un archivo a este área. Tamaño máximo de archivo 20MB.' }),
    headers: { 'Authorization-Token': localStorage.getItem('token') },
  };

  const dropzoneEventHandlers = {
    init: (dz) => {
      dropzone.current = dz;
    },
    success: (file, response) => {
      dropzone.current.removeFile(file);
      setAdjuntos((actuales) => actuales.concat([{ id: response.id, nombre: file.name }]));
    },
    error: (file) => {
      dropzone.current.removeFile(file);
      Swal.fire({
        icon: 'error',
        title: intl.formatMessage({ id: 'solicitudesAbm.adjuntos.error', defaultMessage: 'No se pudo subir el archivo' }),
        confirmButtonText: 'OK',
      });
    },
  };

  const quitarAdjunto = (id) => {
    setAdjuntos((actuales) => actuales.filter((adjunto) => adjunto.id !== id));
  };

  const formularioCompleto = Boolean(formData.movil)
    && formData.detalle.trim() !== ''
    && (!inmovilizadoHabilitado || Boolean(formData.inmovilizado));

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!formularioCompleto) return;

    setLoading(true);
    const datos = {
      movil: formData.movil.id,
      detalle: formData.detalle,
      inmovilizado: inmovilizadoHabilitado ? formData.inmovilizado.value === INMOVILIZADO_SI : null,
      adjuntos: adjuntos.map((adjunto) => adjunto.id),
      fechaHoraTurno: fechaTurnoHabilitada ? formatearFechaHoraTurno(formData.fechaHoraTurno) : null,
    };

    Utils.sendData('/solicitudes', 'POST', datos)
      .then((solicitud) => {
        if (!solicitud) return;
        Swal.fire({
          icon: 'success',
          title: intl.formatMessage({ id: 'solicitudesAbm.guardada.titulo', defaultMessage: 'Solicitud guardada' }),
          text: intl.formatMessage({ id: 'solicitudesAbm.guardada.texto', defaultMessage: 'Se generó la solicitud {numero}.' }, { numero: solicitud.numero }),
          confirmButtonText: 'OK',
        }).then(() => setRedirectTo('/solicitudes'));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  if (redirectTo) return <Redirect push to={redirectTo} />;

  return (
    <React.Fragment>
      {loading && <Loading />}
      <form className="form form-horizontal" onSubmit={handleSubmit}>
        <div className="form-body">
          <div className="card pull-up">
            <div className="card-content">
              <div className="card-body">
                <h4 className="form-section">
                  <i className="la la-file-text"></i>
                  <FormattedMessage id="solicitudesAbm.titulo" defaultMessage="Nueva Solicitud" />
                </h4>

                <div className="form-group row">
                  <label className="col-md-3 label-control col-form-label" htmlFor="movil">
                    <FormattedMessage id="solicitudesAbm.entidad" defaultMessage="Entidad" /> *:
                  </label>
                  <div className="col-md-9">
                    <Select.Async
                      id="movil"
                      name="movil"
                      value={formData.movil}
                      valueKey="id"
                      labelKey="dominio"
                      loadOptions={buscarMoviles}
                      cache={false}
                      onChange={(movil) => setFormData({ ...formData, movil: movil })}
                      placeholder={intl.formatMessage({ id: 'solicitudesAbm.entidad.placeholder', defaultMessage: 'Dominio' })}
                    />
                  </div>
                </div>

                {(inmovilizadoHabilitado || fechaTurnoHabilitada) && (
                  <div className="row">
                    {inmovilizadoHabilitado && (
                      <div className="col-12 col-md-6">
                        <div className="form-group row">
                          <label className="col-md-6 label-control col-form-label" htmlFor="inmovilizado">
                            <FormattedMessage id="correctivosAbm.datos_generales.inmovilizado" defaultMessage="Inmovilizado" /> *:
                          </label>
                          <div className="col-md-6">
                            <Select
                              id="inmovilizado"
                              name="inmovilizado"
                              options={inmovilizadoOptions}
                              value={formData.inmovilizado}
                              valueKey="value"
                              labelKey="label"
                              clearable={false}
                              onChange={(opcion) => setFormData({ ...formData, inmovilizado: opcion })}
                              placeholder={intl.formatMessage({ id: 'solicitudesAbm.inmovilizado.placeholder', defaultMessage: 'Seleccione' })}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    {fechaTurnoHabilitada && (
                      <div className="col-12 col-md-6">
                        <div className="form-group row">
                          <label className="col-md-4 label-control col-form-label" htmlFor="fechaHoraTurno">
                            <FormattedMessage id="solicitudesAbm.fecha_turno" defaultMessage="Fecha de Turno" />:
                          </label>
                          <div className="col-md-8">
                            <DatePicker
                              id="fechaHoraTurno"
                              name="fechaHoraTurno"
                              className="form-control date-picker-placeholder"
                              dateFormat={LocaleFormat.getDateTimePickerFormat()}
                              placeholderText={intl.formatMessage({ id: 'solicitudesAbm.fecha_turno.placeholder', defaultMessage: 'DD/MM/AAAA' })}
                              selected={formData.fechaHoraTurno ? moment(formData.fechaHoraTurno) : null}
                              onChange={(fecha) => setFormData({ ...formData, fechaHoraTurno: fecha ? fecha.format() : null })}
                              minDate={moment()}
                              showTimeSelect
                              timeIntervals={10}
                              popperClassName="datepicker-popper-con-hora"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group row">
                  <label className="col-md-3 label-control col-form-label" htmlFor="detalle">
                    <FormattedMessage id="solicitudesAbm.detalle" defaultMessage="Detalle" /> *:
                  </label>
                  <div className="col-md-9">
                    <textarea
                      id="detalle"
                      name="detalle"
                      className="form-control"
                      rows="5"
                      maxLength={3000}
                      value={formData.detalle}
                      onChange={(event) => setFormData({ ...formData, detalle: event.target.value })}
                      placeholder={intl.formatMessage({ id: 'solicitudesAbm.detalle.placeholder', defaultMessage: 'Describa el problema' })}
                    />
                  </div>
                </div>

                <div className="form-group row">
                  <label className="col-md-3 label-control col-form-label">
                    <FormattedMessage id="solicitudesAbm.adjuntos" defaultMessage="Fotos y adjuntos" />:
                  </label>
                  <div className="col-md-9">
                    <DropzoneComponent config={dropzoneConfig} djsConfig={dropzoneDjsConfig} eventHandlers={dropzoneEventHandlers} />
                    {adjuntos.map((adjunto) => (
                      <div key={adjunto.id} className="mt-1">
                        <i className="la la-paperclip"></i> {adjunto.nombre}
                        <button type="button" className="btn btn-sm btn-link text-danger" onClick={() => quitarAdjunto(adjunto.id)}>
                          <i className="la la-trash"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="alert alert-info mb-0">
                  <FormattedMessage
                    id="solicitudesAbm.aviso_revision"
                    defaultMessage="Tu solicitud la revisa el equipo de mantenimiento. Cuando se genere el ticket o se rechace, te llega una notificación."
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card pull-up">
            <div className="card-content">
              <div className="card-body">
                <div className="text-cd text-right">
                  <button type="submit" className="btn btn-primary mr-1" disabled={loading || !formularioCompleto}>
                    <i className="fa fa-check-circle"></i> <FormattedMessage id="solicitudesAbm.guardar" defaultMessage="Guardar" />
                  </button>
                  <button type="button" className="btn btn-danger" onClick={() => setRedirectTo('/solicitudes')}>
                    <i className="fa fa-times-circle"></i> <FormattedMessage id="solicitudesAbm.cancelar" defaultMessage="Cancelar" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </React.Fragment>
  );
};

export default SolicitudesAbm;
